/**
 * Avatar Component
 * Displays user avatar with fallback to initials or gender icon
 */

import * as React from "react";
import { useState } from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { User, UserRound, X, ZoomIn } from "lucide-react";

import { cn } from "@/lib/utils";

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn("relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full", className)}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn("aspect-square h-full w-full object-cover", className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      "flex h-full w-full items-center justify-center rounded-full bg-muted",
      className
    )}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

// =============================================================================
// PATIENT AVATAR - Custom component for patient photos with gender fallback
// =============================================================================

interface PatientAvatarProps {
  src?: string | null;
  name: string;
  sexo: "M" | "F";
  size?: "sm" | "md" | "lg";
  className?: string;
  clickable?: boolean;
}

const sizeClasses = {
  sm: "h-8 w-8",
  md: "h-10 w-10",
  lg: "h-12 w-12",
};

const iconSizes = {
  sm: "h-4 w-4",
  md: "h-5 w-5",
  lg: "h-6 w-6",
};

/**
 * PatientAvatar - Shows patient photo or gender-based fallback icon
 * Clicking on the avatar (when has photo) opens a lightbox with full-size image
 *
 * @param src - URL of the patient's photo
 * @param name - Patient's name (for alt text)
 * @param sexo - Patient's gender ("M" or "F")
 * @param size - Avatar size (sm, md, lg)
 * @param clickable - Enable click to view full image (default: true when src exists)
 */
export function PatientAvatar({
  src,
  name,
  sexo,
  size = "md",
  className,
  clickable = true,
}: PatientAvatarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const GenderIcon = sexo === "M" ? User : UserRound;
  const iconColorClass = sexo === "M" ? "text-blue-600" : "text-pink-600";
  const bgColorClass =
    sexo === "M" ? "bg-blue-100 dark:bg-blue-950" : "bg-pink-100 dark:bg-pink-950";

  const hasPhoto = !!src;
  const isClickable = clickable && hasPhoto;

  const avatarElement = (
    <div className={cn("relative group", isClickable && "cursor-pointer")}>
      <Avatar className={cn(sizeClasses[size], className)}>
        {src && <AvatarImage src={src} alt={name} />}
        <AvatarFallback className={bgColorClass}>
          <GenderIcon className={cn(iconSizes[size], iconColorClass)} />
        </AvatarFallback>
      </Avatar>
      {/* Zoom indicator on hover */}
      {isClickable && (
        <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
          <ZoomIn className="h-3 w-3 text-white" />
        </div>
      )}
    </div>
  );

  if (!isClickable) {
    return avatarElement;
  }

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={setIsOpen}>
      <DialogPrimitive.Trigger asChild>{avatarElement}</DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content className="fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]">
          {/* Close button */}
          <DialogPrimitive.Close className="absolute -top-10 right-0 rounded-full p-2 text-white/70 hover:text-white hover:bg-white/10 transition-colors">
            <X className="h-6 w-6" />
            <span className="sr-only">Cerrar</span>
          </DialogPrimitive.Close>

          {/* Image container */}
          <div className="relative max-w-[90vw] max-h-[85vh] overflow-hidden rounded-lg shadow-2xl">
            <img src={src} alt={name} className="max-w-full max-h-[85vh] object-contain" />
            {/* Patient name overlay */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
              <p className="text-white font-medium text-lg">{name}</p>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export { Avatar, AvatarImage, AvatarFallback };
